import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { PastedSticker } from 'types'
import { getPastedStickers, pasteSticker } from '../api'
import { Box } from '@mui/material'
import { currentStage } from 'config'
import { useLocation } from 'react-router-dom'

const StickerContext = createContext<{
  pasteSticker: (
    stickerId: string,
    page: string,
    x: number,
    y: number
  ) => Promise<void>
}>({
  pasteSticker: async () => {},
})

const useStickers = () => useContext(StickerContext)

const StickerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [pastedStickers, setPastedStickers] = useState<PastedSticker[]>([])
  const path = useLocation().pathname

  const fetchPastedStickers = useCallback(async () => {
    try {
      const pastedStickers = await getPastedStickers()
      setPastedStickers(pastedStickers)
    } catch (e) {
      console.error(e)
    }
  }, [])

  useEffect(() => {
    fetchPastedStickers()
    const interval = setInterval(fetchPastedStickers, 5000)
    return () => clearInterval(interval)
  }, [fetchPastedStickers])

  const onPasteSticker = useCallback(
    async (stickerId: string, page: string, x: number, y: number) => {
      pasteSticker(stickerId, page, x, y)
      setPastedStickers([
        ...pastedStickers,
        { photoId: stickerId, page, x, y } as PastedSticker, // Add to array immediately, even if API fails
      ])
    },
    [pastedStickers]
  )

  return (
    <StickerContext.Provider value={{ pasteSticker: onPasteSticker }}>
      <Box
        sx={{
          position: 'absolute', // Creates a new positioning context to avoid showing those that are off screen (i.e. made on desktop)
          width: '100vw', // or a specific width (e.g., 800px)
          height: '100vh', // or a specific height (e.g., 600px)
          overflow: 'hidden', // Ensures anything beyond these dimensions is clipped

          pointerEvents: 'none', // Prevents any pointer events on this box
        }}
      >
        {pastedStickers
          .filter(sticker => sticker.page === path)
          .map((sticker, i) => (
            <PastedStickerComponent key={i} sticker={sticker} />
          ))}
      </Box>
      {children}
    </StickerContext.Provider>
  )
}

export { StickerProvider, useStickers }

function PastedStickerComponent(props: { sticker: PastedSticker }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        left: props.sticker.x,
        top: props.sticker.y,
        zIndex: 1,

        width: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        src={`https://jasonandfriends-prod-pictures-bucket.s3.amazonaws.com/processed/${props.sticker.photoId}/thumbnail_${props.sticker.photoId}`}
        alt={props.sticker.photoId}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          display: 'block',
        }}
      />
    </Box>
  )
}
